<template>
  <container>
    <!-- Page title -->
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Settings</h1>
        <p class="pg-title-sub">Manage banners, audio channels and API environment</p>
      </div>
      <router-link to="/admin/system-settings" class="pg-settings-link" title="System Settings">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.6 9a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
        System Settings
      </router-link>
    </div>

    <!-- Banners -->
    <div class="bn-card">
      <div class="bn-header">
        <span class="bn-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </span>
        <div>
          <h2 class="bn-title">Banners</h2>
          <p class="bn-subtitle">Manage promotional banners displayed in the mobile app</p>
        </div>
        <button class="bn-add-btn" @click="showBannerModal('Add')" title="Add banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          Add Banner
        </button>
      </div>

      <!-- Skeleton shimmer while loading -->
      <div v-if="bannersLoading" class="bn-grid">
        <div v-for="n in 3" :key="'sk-'+n" class="bn-skeleton">
          <div class="bn-sk-header">
            <div class="bn-sk-badge sk-shimmer"></div>
            <div class="bn-sk-actions">
              <div class="bn-sk-btn sk-shimmer"></div>
              <div class="bn-sk-btn sk-shimmer"></div>
            </div>
          </div>
          <div class="bn-sk-image sk-shimmer"></div>
        </div>
      </div>

      <!-- Banner grid -->
      <div v-else class="bn-grid">
        <div v-for="Banner in bannerList" :key="Banner.id" class="bn-item">
          <div class="bn-item-header">
            <span class="bn-type-badge">{{ Banner.Type }}</span>
            <div class="bn-actions">
              <button class="bn-action-btn" @click="showBannerModal('Edit', Banner)" title="Edit banner">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <button class="bn-action-btn bn-action-btn--delete" @click="confirmDeleteBanner(Banner)" title="Delete banner">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
          <div class="bn-image-wrap">
            <img :src="Banner.ImageUrl" class="bn-image" />
          </div>
        </div>

        <div v-if="!bannerList || bannerList.length === 0" class="bn-empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c8c8d8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          <p>No banners yet. Click <strong>Add Banner</strong> to create one.</p>
        </div>
      </div>

      <add-edit-banner :show="showAddEditBannerModal" :type="bannerOpr" :bannerData="bannerData" @saved="onSaved" @closed="onClosed"></add-edit-banner>
    </div>

  <!-- Audio Channels A–D -->
  <div class="ac-card">
    <div class="ac-header">
      <span class="ac-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        </svg>
      </span>
      <div>
        <h2 class="ac-title">Audio Channels</h2>
        <p class="ac-subtitle">Configure live stream URLs for channels A–D. The mobile app selects the best available channel.</p>
      </div>
    </div>

    <!-- 2-column channel grid: A/B left, C/D right -->
    <div class="ac-grid">
      <!-- Left column: A & B -->
      <div class="ac-col">
        <div class="ac-channel-row" v-for="ch in [
          { key: 'A', saved: audioChannelA, error: linkAError },
          { key: 'B', saved: audioChannelB, error: linkBError }
        ]" :key="ch.key">
          <div class="ac-row-label">Channel {{ ch.key }}</div>
          <div class="ac-row-body">
            <div class="ac-input-group">
              <input class="ac-input" type="text" :name="'channelLink' + ch.key"
                v-model="$data['channelLink' + ch.key]"
                :placeholder="'Stream URL for Channel ' + ch.key" />
              <button class="ac-add-btn" v-if="ch.key === 'A'" @click="onAddChannelA()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Save
              </button>
              <button class="ac-add-btn" v-else @click="onAddChannelB()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Save
              </button>
            </div>
            <div v-if="ch.error" class="ac-error">{{ ch.error }}</div>
            <div v-if="ch.saved" class="ac-url-row">
              <span class="ac-url-badge">Live</span>
              <a :href="ch.saved" target="blank" class="ac-url-text">{{ ch.saved }}</a>
              <button class="ac-play-btn" @click="playChannel(ch.saved, ch.key)" :title="playingChannel === ch.key ? 'Stop' : 'Play channel ' + ch.key">
                <svg v-if="playingChannel !== ch.key" width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M2 1.5l9 4.5-9 4.5V1.5z"/></svg>
                <svg v-else width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="1.5" width="3" height="9" rx="1"/><rect x="7" y="1.5" width="3" height="9" rx="1"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="ac-col-divider"></div>

      <!-- Right column: C & D -->
      <div class="ac-col">
        <div class="ac-channel-row" v-for="ch in [
          { key: 'C', saved: audioChannelC, error: linkCError },
          { key: 'D', saved: audioChannelD, error: linkDError }
        ]" :key="ch.key">
          <div class="ac-row-label">Channel {{ ch.key }}</div>
          <div class="ac-row-body">
            <div class="ac-input-group">
              <input class="ac-input" type="text" :name="'channelLink' + ch.key"
                v-model="$data['channelLink' + ch.key]"
                :placeholder="'Stream URL for Channel ' + ch.key" />
              <button class="ac-add-btn" v-if="ch.key === 'C'" @click="onAddChannelC()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Save
              </button>
              <button class="ac-add-btn" v-else @click="onAddChannelD()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Save
              </button>
            </div>
            <div v-if="ch.error" class="ac-error">{{ ch.error }}</div>
            <div v-if="ch.saved" class="ac-url-row">
              <span class="ac-url-badge">Live</span>
              <a :href="ch.saved" target="blank" class="ac-url-text">{{ ch.saved }}</a>
              <button class="ac-play-btn" @click="playChannel(ch.saved, ch.key)" :title="playingChannel === ch.key ? 'Stop' : 'Play channel ' + ch.key">
                <svg v-if="playingChannel !== ch.key" width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M2 1.5l9 4.5-9 4.5V1.5z"/></svg>
                <svg v-else width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="1.5" width="3" height="9" rx="1"/><rect x="7" y="1.5" width="3" height="9" rx="1"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Action Button -->
  <div class="ab-card">
    <div class="ab-header">
      <span class="ab-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 21a8 8 0 0 1 16 0"/>
        </svg>
      </span>
      <div class="ab-header-text">
        <h2 class="ab-title">Action Button</h2>
        <p class="ab-subtitle">Configure the circular image and tap-through URL shown in the mobile app</p>
      </div>
      <div class="ab-header-actions">
        <button class="ab-clear-btn" :disabled="!hasActionButtonData || actionButtonSaving || actionButtonUploading || actionButtonLoading" @click="clearActionButtonSettings">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
          Clear
        </button>
        <button class="ab-save-btn" :disabled="actionButtonSaving || actionButtonUploading || actionButtonLoading" @click="saveActionButtonSettings">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          {{ actionButtonSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <div v-if="actionButtonLoading" class="ab-loading-grid">
      <div class="ab-preview-skeleton sk-shimmer"></div>
      <div class="ab-loading-fields">
        <div class="ab-sk-line ab-sk-line--wide sk-shimmer"></div>
        <div class="ab-sk-line sk-shimmer"></div>
      </div>
    </div>

    <div v-else class="ab-body">
      <div class="ab-preview-col">
        <div class="ab-preview">
          <img v-if="actionButtonPreviewSrc" :src="actionButtonPreviewSrc" class="ab-preview-img" alt="Action button preview" />
          <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M4 21a8 8 0 0 1 16 0"/>
          </svg>
        </div>
        <span class="ab-preview-label">Preview</span>
      </div>

      <div class="ab-fields">
        <div class="ab-upload-row">
          <label class="ab-file-label">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>{{ actionButtonFile ? actionButtonFile.name : 'Choose image (.jpg, .png, .webp)' }}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" @change="onActionButtonFileSelect" class="ab-file-hidden" />
          </label>
          <button class="ab-upload-btn" :disabled="!actionButtonFile || actionButtonUploading" @click="uploadActionButtonImage">
            <span v-if="!actionButtonUploading">Upload</span>
            <span v-else>{{ actionButtonUploadPct }}%</span>
          </button>
        </div>

        <progress v-if="actionButtonUploading" class="ab-progress" max="100" :value="actionButtonUploadPct"></progress>
        <div v-if="actionButtonImageError" class="ab-alert ab-alert--error">{{ actionButtonImageError }}</div>

        <div class="ab-url-field">
          <label class="ab-label">Target URL</label>
          <div class="ab-input-wrap">
            <input class="ab-input" type="text" v-model="actionButtonUrl" placeholder="https://example.com/action" />
            <button class="ab-open-btn" :disabled="!isStrictHttpUrl(actionButtonUrl)" @click="openActionButtonUrl" title="Open URL in new tab">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </button>
          </div>
          <div v-if="actionButtonUrlError" class="ab-field-error">{{ actionButtonUrlError }}</div>
        </div>

        <div v-if="actionButtonImageUrl" class="ab-url-row">
          <span class="ab-url-badge">Image</span>
          <a :href="actionButtonImageUrl" target="_blank" class="ab-url-text">{{ actionButtonImageUrl }}</a>
        </div>

        <div v-if="!hasActionButtonData" class="ab-empty-note">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          No action button is configured. The mobile app will not show this button.
        </div>

        <div v-if="actionButtonSaveSuccess" class="ab-success-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          {{ actionButtonSuccessMessage }}
        </div>
        <div v-if="actionButtonSaveError" class="ab-alert ab-alert--error">{{ actionButtonSaveError }}</div>
      </div>
    </div>
  </div>

  <image-crop-preview-modal
    modal-name="actionButtonCropModal"
    :show="actionButtonCropModalVisible"
    :source-url="actionButtonCropSourceUrl"
    :file-name="actionButtonCropFileName"
    :aspect-ratio="1"
    :output-width="600"
    :output-height="600"
    title="Preview Action Button"
    subtitle="Crop the circular image area before upload"
    @cropped="onActionButtonImageCropped"
    @closed="onActionButtonCropClosed"
  ></image-crop-preview-modal>

  <!-- Fallback + Mode side by side -->
  <div class="bottom-row">

    <!-- Default Fallback Channel -->
    <div class="fbc-card">
      <div class="fbc-header">
        <div class="fbc-header-left">
          <span class="fbc-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18V5l12-2v13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke="currentColor" stroke-width="1.8"/>
              <circle cx="18" cy="16" r="3" stroke="currentColor" stroke-width="1.8"/>
            </svg>
          </span>
          <div>
            <h2 class="fbc-title">Default Fallback Channel</h2>
            <p class="fbc-subtitle">Used by the mobile app when no race-specific audio channel is assigned</p>
          </div>
        </div>
      </div>

      <div v-if="fallbackChannelUrl" class="fbc-url-row">
        <span class="fbc-url-label">Active</span>
        <a :href="fallbackChannelUrl" target="blank" class="fbc-url-text">{{ fallbackChannelUrl }}</a>
        <button class="fbc-play-btn" @click="playChannel(fallbackChannelUrl, 'FALLBACK')" :title="playingChannel === 'FALLBACK' ? 'Stop' : 'Play'">
          <svg v-if="playingChannel !== 'FALLBACK'" width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M2 1.5l9 4.5-9 4.5V1.5z"/></svg>
          <svg v-else width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="1.5" width="3" height="9" rx="1"/><rect x="7" y="1.5" width="3" height="9" rx="1"/></svg>
        </button>
      </div>
      <div v-else class="fbc-empty">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        No fallback URL saved
      </div>

      <div class="fbc-upload-area">
        <label class="fbc-file-label">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span>{{ fallbackFile ? fallbackFile.name : 'Choose audio file (.mp3, .m4a, .aac)' }}</span>
          <input type="file" accept="audio/mpeg,audio/mp4,audio/aac,.mp3,.m4a,.aac" @change="onFallbackFileSelect" class="fbc-file-hidden" />
        </label>
        <button class="fbc-upload-btn" :disabled="!fallbackFile || fallbackUploading" @click="onUploadFallback">
          <span v-if="!fallbackUploading">Upload</span>
          <span v-else class="fbc-uploading-text">{{ fallbackUploadPct }}%</span>
        </button>
      </div>

      <div v-if="fallbackUploading" class="fbc-progress">
        <div class="fbc-progress-bar" :style="{ width: fallbackUploadPct + '%' }"></div>
      </div>
      <div v-if="fallbackUploadError" class="fbc-alert fbc-alert--error">{{ fallbackUploadError }}</div>

      <div v-if="fallbackSaveSuccess" class="fbc-success-badge fbc-save-inline">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        Uploaded &amp; saved to Firebase
      </div>
      <span v-if="fallbackSaveError" class="fbc-alert fbc-alert--inline">{{ fallbackSaveError }}</span>
    </div>

    <!-- API Environment -->
    <div class="mode-card">
      <!-- Header: icon + title + subtitle -->
      <div class="mode-header">
        <span class="mode-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
        </span>
        <div class="mode-header-text">
          <h2 class="mode-title">API Environment</h2>
          <p class="mode-subtitle">Switch between Live and Staging environments</p>
        </div>
      </div>

      <!-- Toggle row: below the header -->
      <div class="mode-toggle-row">
        <lv-toggle v-model="alert" @change="onToggle()" />
        <span class="mode-env-label" :class="alert ? 'mode-env-label--staging' : 'mode-env-label--live'">
          {{ alert ? 'Staging' : 'Live' }}
        </span>
      </div>

      <div class="mode-warning">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        Do not switch API environment while a live run is happening
      </div>
    </div>

  </div>
  </container>
</template>
<script>
import Container from "@/components/Container"
import { mapActions, mapGetters } from "vuex"
import S3UploadService from "@/services/s3Upload"
import SettingsService from "@/services/settings"
import VultrUploadService from "@/services/VultrUploadService"
import ActionButtonSettingsService from "@/services/actionButtonSettingsService"
import ImageCropPreviewModal from '@/components/Forms/ImageCropPreviewModal'
import TextField from '@/components/Forms/TextField'
import AddEditBanner from './BannerManger/AddEditBanner'
import LvToggle from '@/components/Forms/LvToggle'
import { db } from '../../../firebase'
import Vue from 'vue'
import axios from 'axios'
import VuejsDialog from 'vuejs-dialog'

Vue.use(VuejsDialog)

let dialogOptions = {
  html: false,
  loader: false,
  reverse: false,
  cancelText: 'No',
  okText: 'Yes',
  animation: 'zoom',
  type: 'basic'
}

export default {
  name: "settings",
  data() {
    return {
      hasError: false,
      errorMessage: "Fill in the photo with size below 1 mb",
      choosed: true,
      file: {},
      url: null,
      aspectRatioError: null,
      uploadingBar: false,
      uploadPercentage: 0,
      interval: null,
      suceessmessage: null,
      banner:null,
      bannerlink:null,
      msg:'Are you sure want to remove banner?',
      link:null,
      suceessLinkMessage:false,
      linkError:null,


      hasUsError: false,
      usErrorMessage: "Fill in the photo with size below 1 mb",
      usChoosed: true,          
      usFile: {},
      usUrl:null,
      usAspectRatioError: null,
      usSuceessMsg: null,  
      USbanner:null,
      USbannerlink:null,
      usLink:null,
      usSuceessLinkMessage:false,
      usLinkError:null,    
     
      channelLinkA:null,
      channelLinkB:null,
      channelLinkC:null,      
      channelLinkD:null,
      linkAError:null,
      linkBError:null,
      linkCError:null,
      linkDError:null,
      
      // status:'Staging'
      alert: false,
      audioChannelA:null,
      audioChannelB:null,
      audioChannelC:null,
      audioChannelD:null,

      bannersLoading: false,
      bannerList:[],
      showAddEditBannerModal:false,
      bannerData:{},
      bannerOpr:'',

      // Playback state
      playingChannel: null,  // 'A' | 'B' | 'C' | 'D' | 'FALLBACK' | null
      _audioPlayer: null,    // shared Audio instance

      // Default Fallback Channel
      fallbackChannelUrl: null,
      fallbackFile: null,
      fallbackUploading: false,
      fallbackUploadPct: 0,
      fallbackUploadedUrl: null,
      fallbackUploadError: null,
      fallbackSaveSuccess: false,
      fallbackSaveError: null,

      // Action Button
      actionButtonLoading: true,
      actionButtonSaving: false,
      actionButtonUploading: false,
      actionButtonUploadPct: 0,
      actionButtonFile: null,
      actionButtonUrl: '',
      actionButtonImageUrl: '',
      actionButtonLocalPreviewUrl: '',
      actionButtonCropModalVisible: false,
      actionButtonCropSourceUrl: '',
      actionButtonCropFileName: '',
      actionButtonUrlError: null,
      actionButtonImageError: null,
      actionButtonSaveError: null,
      actionButtonSaveSuccess: false,
      actionButtonSuccessMessage: 'Action button saved'

    }
  },
  computed: {
    actionButtonPreviewSrc () {
      return this.actionButtonLocalPreviewUrl || this.actionButtonImageUrl
    },
    hasActionButtonData () {
      return Boolean(
        this.actionButtonUrl ||
        this.actionButtonImageUrl ||
        this.actionButtonLocalPreviewUrl ||
        this.actionButtonFile
      )
    }
    // alert () {
    //   return this.$store.getters['Alert/alert']
    // }
  },
  methods: {
     ...mapActions({
      setAlert: 'Alert/setAlert',
    }),

    confirmDeleteBanner(banner) {
      this.$dialog.confirm("Are you sure want to remove banner?", dialogOptions)
        .then((dialog) => {
          this.deleteBanner(banner)
        })
    },

    async onToggle(){
      if(this.$envStatus ==='Live'){
          this.alert = false
          Vue.prototype.$envStatus = 'Staging'
          Vue.prototype.$isStage = 0
          Vue.prototype.$fbDbEntry = 'racers'
           Vue.http.options.root = 'https://app.liverunapp.com/api'
          window.localStorage.setItem('envStatus','Staging')
          this.setAlert(false)
          this.$router.go()
      }else if(this.$envStatus ==='Staging'){
          this.alert = true
          Vue.prototype.$envStatus = 'Live' 
          Vue.prototype.$isStage = 1
          Vue.prototype.$fbDbEntry = 'racers-test'
           if (await this.checkDevServer()) {
            Vue.http.options.root = 'https://devapp.liverunapp.com/api'
          } else {
            Vue.http.options.root = 'https://liverunapi.npit.xyz/api'
          }

          //Vue.http.options.root = 'https://devapp.liverunapp.com/api'
          window.localStorage.setItem('envStatus','Live')
          this.setAlert(true)
          this.$router.go()
      }
    },
    async checkDevServer() {
        try {
          let data = await axios.get('https://devapp.liverunapp.com/')
          return true
        } catch (err) {
          return false
        }
      },
  
    changeFileHandle(e) {
    
      this.suceessmessage = null
      
      const originalFile = e.target.files[0]
      this.url = URL.createObjectURL(originalFile)
      const file = new FormData()
      file.append("file", originalFile)
      const size = originalFile.size / 1024
      if (size > 1024) {
        this.hasError = true
        this.aspectRatioError = null
        this.choosed = true
      } else {
        this.choosed = false

        this.file.name = originalFile.name
        this.file.data = originalFile
      }
    },
    onupload() {
      let aspectRatio =
        document.getElementById("image").clientWidth /
        document.getElementById("image").clientHeight
        aspectRatio = Math.round(aspectRatio)
      if (aspectRatio == 3) {
        this.aspectRatioError = null
         this.hasError = false
        this.uploadingBar = true
        this.startBar()
        S3UploadService.uploadToS3(this.file,"NORMAL", (data, err) => {
          this.stopBar()
          this.uploadingBar = false
          this.uploadPercentage = 0
          this.suceessmessage = true
          this.url = null
          this.choosed = true
          setTimeout(()=>{
            this.suceessmessage = false            
          },2000)

          this.getExistingBanner()
        })
      } else {
        this.aspectRatioError = "Please upload 3:1 aspect ratio Image"
        this.choosed = true
          this.errorMessage = null
        
      }
    },
    async onAddBannerLink(){              
         const valid =  await this.isUrlValid(this.link)     
              if(valid){
                this.linkError = null
                S3UploadService.updateBannerLink(this.link,'NORMAL',(data,err)=>{
                  this.suceessLinkMessage = true
                    setTimeout(()=>{
                    this.suceessLinkMessage = false            
                  },2000)
                   this.getExistingBanner()
                })
              }else{
                this.linkError = 'Banner link is not valid or empty!'
              }
    },    
    async onAddUSBannerLink(){              
         const valid =  await this.isUrlValid(this.usLink)     
              if(valid){
                this.usLinkError = null
                S3UploadService.updateBannerLink(this.usLink,'US',(data,err)=>{
                  this.usSuceessLinkMessage = true
                    setTimeout(()=>{
                    this.usSuceessLinkMessage = false            
                  },2000)
                   this.getExistingBanner()
                })
              }else{
                this.usLinkError = 'Banner link is not valid or empty!'
              }
    },
    selectNoBanner(banner) {
      S3UploadService.selectNoImage(banner, (data, err) => {
        this.getExistingBanner()
      })
    },
    startBar() {
      this.interval = setInterval(() => {
        if (this.uploadPercentage < 95) this.uploadPercentage += 10
      }, 200)
    },
    stopBar() {
      clearInterval(this.interval)
      this.uploadPercentage = 100
    },
    isUrlValid(userInput) {
       if(userInput == null)
            return false
        var res = userInput.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g)
        if(res == null)
            return false
        else
            return true
    },
  changeUSFileHandle(e) {
    
      this.usSuceessMsg = null
      
      const originalFile = e.target.files[0]
      this.usUrl = URL.createObjectURL(originalFile)
      const file = new FormData()
      file.append("file", originalFile)
      const size = originalFile.size / 1024
      if (size > 1024) {
        this.hasUsError = true
        this.usAspectRatioError = null
        this.usChoosed = true
      } else {
        this.usChoosed = false

        this.usFile.name = originalFile.name
        this.usFile.data = originalFile
      }
    },
    onuploadUsBanner() {
      let aspectRatio =
        document.getElementById("us-image").clientWidth /
        document.getElementById("us-image").clientHeight
        aspectRatio = Math.round(aspectRatio)
      if (aspectRatio == 3) {
        this.usAspectRatioError = null
         this.hasUsError = false
        this.uploadingBar = true
        this.startBar()
        S3UploadService.uploadToS3(this.usFile,'US', (data, err) => {
          this.stopBar()
          this.uploadingBar = false
          this.uploadPercentage = 0
          this.usSuceessMsg = true
          this.usUrl = null
          this.usChoosed = true
          setTimeout(()=>{
            this.usSuceessMsg = false            
          },2000)

          this.getExistingBanner()
        })
      } else {
        this.usAspectRatioError = "Please upload 3:1 aspect ratio Image"
        this.usChoosed = true
        this.usErrorMessage = null
        
      }
    },

    
    getExistingBanner(){
       S3UploadService.getUploadedBanner((data) => {
        if (!data || data instanceof Error) return
        this.banner = data.view && data.view.top_banner_image_url
        this.bannerlink = data.view && data.view.top_banner_link_url
        this.USbanner = data.view_us && data.view_us.top_banner_image_url
        this.USbannerlink = data.view_us && data.view_us.top_banner_link_url
    })
    },
    async onAddChannelA(){
      const valid =  await this.isUrlValid(this.channelLinkA)     
      if(valid){
        this.linkAError = null
        await SettingsService.update({audioChannelA:this.channelLinkA})
        this.audioChannelA = this.channelLinkA
      }else{
        this.linkAError = 'Channel A link is not valid or empty!'
      }
    },
    async onAddChannelB(){
     const valid =  await this.isUrlValid(this.channelLinkB)     
      if(valid){
        this.linkBError = null
        await SettingsService.update({audioChannelB:this.channelLinkB})
        this.audioChannelB = this.channelLinkB
      }else{
        this.linkBError = 'Channel B link is not valid or empty!'
      }
    },
    async onAddChannelC(){
     const valid =  await this.isUrlValid(this.channelLinkC)     
      if(valid){
        this.linkCError = null
        await SettingsService.update({audioChannelC:this.channelLinkC})
        this.audioChannelC = this.channelLinkC
      }else{
        this.linkCError = 'Channel C link is not valid or empty!'
      }
    },
     async onAddChannelD(){
     const valid =  await this.isUrlValid(this.channelLinkD)     
      if(valid){
        this.linkDError = null
        await SettingsService.update({audioChannelD:this.channelLinkD})
        this.audioChannelD = this.channelLinkD
      }else{
        this.linkDError = 'Channel D link is not valid or empty!'
      }
    },
    async getExistingChannels(){
       const channels = await SettingsService.get()
       if(channels){
        this.audioChannelA  = channels.data.audioChannelA
        this.audioChannelB  = channels.data.audioChannelB
        this.audioChannelC  = channels.data.audioChannelC
        this.audioChannelD  = channels.data.audioChannelD
       }      
    },

    // ── Audio playback ────────────────────────────────────────────────────────
    playChannel (url, channelKey) {
      if (this.playingChannel === channelKey) {
        // Stop the currently playing channel
        this.stopChannel()
        return
      }
      // Stop any existing playback first
      this.stopChannel()
      try {
        this._audioPlayer = new Audio(url)
        this._audioPlayer.play()
        this.playingChannel = channelKey
        // Reset icon when audio ends naturally
        this._audioPlayer.addEventListener('ended', () => {
          this.playingChannel = null
          this._audioPlayer = null
        })
      } catch (e) {
        console.error('Failed to play channel', channelKey, e)
      }
    },
    stopChannel () {
      if (this._audioPlayer) {
        this._audioPlayer.pause()
        this._audioPlayer = null
      }
      this.playingChannel = null
    },

    // ── Default Fallback Channel ──────────────────────────────────────────────
    onFallbackFileSelect (e) {
      var original = e.target.files[0]
      if (!original) return
      this.fallbackFile = { name: original.name, data: original }
      this.fallbackUploadedUrl = null
      this.fallbackUploadError = null
      this.fallbackSaveSuccess = false
      this.fallbackSaveError = null
    },
    async onUploadFallback () {
      if (!this.fallbackFile) return
      this.fallbackUploading = true
      this.fallbackUploadPct = 0
      this.fallbackUploadError = null
      this.fallbackUploadedUrl = null
      this.fallbackSaveSuccess = false
      this.fallbackSaveError = null
      try {
        var url = await VultrUploadService.uploadFallbackAudio(
          this.fallbackFile,
          (pct) => { this.fallbackUploadPct = pct }
        )
        this.fallbackUploadedUrl = url
        // Automatically save the uploaded URL to Firebase
        await this.saveFallbackUrl()
      } catch (err) {
        console.error('Fallback upload error', err)
        this.fallbackUploadError = 'Upload failed: ' + (err.message || 'Unknown error')
      } finally {
        this.fallbackUploading = false
      }
    },
    async saveFallbackUrl () {
      this.fallbackSaveSuccess = false
      this.fallbackSaveError = null
      var url = this.fallbackUploadedUrl
      if (!url) return
      try {
        await db.ref('settings/fallbackChannelUrl').set(url)
        this.fallbackChannelUrl = url
        this.fallbackSaveSuccess = true
        var self = this
        setTimeout(function () { self.fallbackSaveSuccess = false }, 3000)
      } catch (err) {
        console.error('Failed to save fallback URL to Firebase', err)
        this.fallbackSaveError = 'Save failed: ' + (err.message || 'Unknown error')
      }
    },
    loadFallbackChannelUrl () {
      var self = this
      db.ref('settings/fallbackChannelUrl').once('value').then(function (snapshot) {
        var val = snapshot.val()
        if (val && typeof val === 'string' && val.length > 0) {
          self.fallbackChannelUrl = val
        }
      }).catch(function (err) {
        console.error('Failed to load fallbackChannelUrl from Firebase', err)
      })
    },
    async loadActionButtonSettings () {
      this.actionButtonLoading = true
      this.actionButtonSaveError = null
      this.actionButtonImageError = null
      this.actionButtonUrlError = null
      try {
        var response = await ActionButtonSettingsService.get()
        var data = response && response.data ? response.data : {}
        this.actionButtonUrl = data.action_button_url || ''
        this.actionButtonImageUrl = data.action_button_image_url || ''
      } catch (err) {
        console.error('Failed to load action button settings', err)
        this.actionButtonSaveError = 'Failed to load action button settings'
      } finally {
        this.actionButtonLoading = false
      }
    },
    onActionButtonFileSelect (e) {
      var original = e.target.files[0]
      this.actionButtonImageError = null
      this.actionButtonSaveSuccess = false
      this.actionButtonSaveError = null
      if (!original) return
      if (!original.type || original.type.indexOf('image/') !== 0) {
        this.actionButtonFile = null
        this.actionButtonImageError = 'Please choose a valid image file.'
        e.target.value = ''
        return
      }

      if (this.actionButtonCropSourceUrl) {
        URL.revokeObjectURL(this.actionButtonCropSourceUrl)
      }
      this.actionButtonCropFileName = original.name
      this.actionButtonCropSourceUrl = URL.createObjectURL(original)
      this.actionButtonCropModalVisible = true
      e.target.value = ''
    },
    onActionButtonImageCropped (payload) {
      if (!payload || !payload.blob) return
      if (this.actionButtonLocalPreviewUrl) {
        URL.revokeObjectURL(this.actionButtonLocalPreviewUrl)
      }
      this.actionButtonFile = { name: payload.name, data: payload.blob }
      this.actionButtonLocalPreviewUrl = payload.previewUrl
      this.actionButtonImageUrl = ''
      this.actionButtonImageError = null
      this.actionButtonCropModalVisible = false
      if (this.actionButtonCropSourceUrl) {
        URL.revokeObjectURL(this.actionButtonCropSourceUrl)
        this.actionButtonCropSourceUrl = ''
      }
      this.actionButtonCropFileName = ''
    },
    onActionButtonCropClosed () {
      this.actionButtonCropModalVisible = false
      if (this.actionButtonCropSourceUrl) {
        URL.revokeObjectURL(this.actionButtonCropSourceUrl)
        this.actionButtonCropSourceUrl = ''
      }
      this.actionButtonCropFileName = ''
    },
    async uploadActionButtonImage () {
      if (!this.actionButtonFile) return
      this.actionButtonUploading = true
      this.actionButtonUploadPct = 0
      this.actionButtonImageError = null
      this.actionButtonSaveSuccess = false
      this.actionButtonSaveError = null
      try {
        var url = await VultrUploadService.uploadActionButtonImage(
          this.actionButtonFile,
          (pct) => { this.actionButtonUploadPct = pct }
        )
        this.actionButtonImageUrl = url
        this.actionButtonFile = null
        this.actionButtonUploadPct = 100
      } catch (err) {
        console.error('Action button image upload error', err)
        this.actionButtonImageError = 'Upload failed: ' + (err.message || 'Unknown error')
      } finally {
        this.actionButtonUploading = false
      }
    },
    isStrictHttpUrl (value) {
      if (!value) return false
      try {
        var parsed = new URL(String(value).trim())
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      } catch (err) {
        return false
      }
    },
    openActionButtonUrl () {
      if (!this.isStrictHttpUrl(this.actionButtonUrl)) return
      window.open(String(this.actionButtonUrl).trim(), '_blank', 'noopener')
    },
    async saveActionButtonSettings () {
      this.actionButtonUrlError = null
      this.actionButtonImageError = null
      this.actionButtonSaveError = null
      this.actionButtonSaveSuccess = false

      if (!this.isStrictHttpUrl(this.actionButtonUrl)) {
        this.actionButtonUrlError = 'Enter a valid HTTP or HTTPS URL.'
        return
      }
      if (!this.isStrictHttpUrl(this.actionButtonImageUrl)) {
        this.actionButtonImageError = 'Upload an image before saving.'
        return
      }

      this.actionButtonSaving = true
      try {
        var response = await ActionButtonSettingsService.update({
          action_button_url: String(this.actionButtonUrl).trim(),
          action_button_image_url: String(this.actionButtonImageUrl).trim()
        })
        var data = response && response.data ? response.data : {}
        this.actionButtonUrl = data.action_button_url || this.actionButtonUrl
        this.actionButtonImageUrl = data.action_button_image_url || this.actionButtonImageUrl
        this.actionButtonSuccessMessage = 'Action button saved'
        this.actionButtonSaveSuccess = true
        var self = this
        setTimeout(function () { self.actionButtonSaveSuccess = false }, 3000)
      } catch (err) {
        console.error('Failed to save action button settings', err)
        var message = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : (err.message || 'Unknown error')
        this.actionButtonSaveError = 'Save failed: ' + message
      } finally {
        this.actionButtonSaving = false
      }
    },
    async clearActionButtonSettings () {
      this.actionButtonUrlError = null
      this.actionButtonImageError = null
      this.actionButtonSaveError = null
      this.actionButtonSaveSuccess = false

      this.actionButtonSaving = true
      try {
        await ActionButtonSettingsService.update({
          action_button_url: '',
          action_button_image_url: ''
        })
        this.actionButtonUrl = ''
        this.actionButtonImageUrl = ''
        this.actionButtonFile = null
        this.actionButtonUploadPct = 0
        if (this.actionButtonLocalPreviewUrl) {
          URL.revokeObjectURL(this.actionButtonLocalPreviewUrl)
          this.actionButtonLocalPreviewUrl = ''
        }
        this.actionButtonSuccessMessage = 'Action button cleared'
        this.actionButtonSaveSuccess = true
        var self = this
        setTimeout(function () { self.actionButtonSaveSuccess = false }, 3000)
      } catch (err) {
        console.error('Failed to clear action button settings', err)
        var message = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : (err.message || 'Unknown error')
        this.actionButtonSaveError = 'Clear failed: ' + message
      } finally {
        this.actionButtonSaving = false
      }
    },
    getBannerList(){
        this.bannersLoading = true
        const AuthStr = 'Bearer '.concat(window.localStorage.getItem('default-auth-token'))
        axios.get(this.$expressApi+`banners?staging=`+this.$isStage, { headers: { Authorization: AuthStr } })
          .then(response => {
            console.log("response---->",response.data)
            if(response.data != undefined && response.data != null){
              this.bannerList = response.data
            }
          }).catch(e => {
            console.log("Error from Api -->",e)
          }).finally(() => {
            this.bannersLoading = false
          })
    },
    deleteBanner(banner){
      axios.post(this.$expressApi+`deleteBanner?staging=`+this.$isStage,banner)
        .then(response =>{ 
          console.log("delete response---->>",response.data)
          this.getBannerList()
        }).catch(e => {
          console.log("Error from Api -->",e)
      })
      
    },
    showBannerModal(type,data){
      this.bannerData = {}
      if(type == 'Edit'){
         this.bannerData = data
      }     
      this.bannerOpr = type
      this.showAddEditBannerModal = true
    },
    onClosed(){
      this.showAddEditBannerModal=false 
    },
    onSaved(){
      this.getBannerList()
    }

  },
  created() {
    if(this.$envStatus == 'Staging'){
      this.alert = false
    }else{
      this.alert = true
    }
  this.getBannerList()
   this.getExistingBanner()
   this.getExistingChannels()
   this.loadFallbackChannelUrl()
   this.loadActionButtonSettings()
  },
  watch: {},
  components: {
    Container,
    TextField,
    AddEditBanner,
    LvToggle,
    ImageCropPreviewModal
  },
  beforeDestroy () {
    this.stopChannel()
    if (this.actionButtonLocalPreviewUrl) {
      URL.revokeObjectURL(this.actionButtonLocalPreviewUrl)
    }
    if (this.actionButtonCropSourceUrl) {
      URL.revokeObjectURL(this.actionButtonCropSourceUrl)
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

/* ── Global interactive cursor ─────────────────────────────── */
button, [role="button"], label[for], select, a,
.lv-toggle, .bn-action-btn, .pg-settings-link,
.ac-save-btn, .ac-play-btn, .fbc-upload-btn, .fbc-save-btn,
.bn-add-btn, .bn-action-btn, .mode-toggle-row .lv-toggle,
.ab-save-btn, .ab-clear-btn, .ab-upload-btn, .ab-open-btn {
  cursor: pointer;
}

/* ── Purple button hover glow shadow ───────────────────────── */
.btn--primary:hover:not(:disabled),
.ac-save-btn:hover:not(:disabled),
.fbc-upload-btn:hover:not(:disabled),
.fbc-save-btn:hover:not(:disabled),
.ab-save-btn:hover:not(:disabled),
.ab-upload-btn:hover:not(:disabled),
.bn-add-btn:hover:not(:disabled),
.bm-save-btn:hover:not(:disabled),
.bm-upload-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(91, 79, 207, 0.38);
  transform: translateY(-1px);
}
.btn--primary, .ac-save-btn, .fbc-upload-btn, .fbc-save-btn,
.ab-save-btn, .ab-upload-btn,
.bn-add-btn, .bm-save-btn, .bm-upload-btn {
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
}

#preview {
  width: 50%;
}
.btn-play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #413b94;
  background: #fff;
  color: #413b94;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.btn-play:hover {
  background: #413b94;
  color: #fff;
}

/* ── Audio Channels Card (ac-) ───────────────────────────────── */
.ac-card {
  margin-top: 2rem;
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
/* 2-column grid inside Audio Channels card */
.ac-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: start;
}
.ac-col {
  min-width: 0;
}
.ac-col-divider {
  width: 1px;
  background: #f0f0f7;
  margin: 0 1.5rem;
  align-self: stretch;
}
.ac-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}
.ac-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}
.ac-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 3px 0;
  letter-spacing: -0.01em;
}
.ac-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}
/* Channel rows */
.ac-channel-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid #f2f2f7;
}
.ac-channel-row:first-of-type { border-top: none; }
.ac-row-label {
  font-size: 12px;
  font-weight: 700;
  color: #5b4fcf;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  min-width: 64px;
  padding-top: 10px;
  flex-shrink: 0;
}
.ac-row-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ac-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}
.ac-input {
  flex: 1;
  border: 1.5px solid #e0e0ea;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #1a1a2e;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.ac-input:focus { border-color: #5b4fcf; }
.ac-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.ac-add-btn:hover { background: #4338ca; }
.ac-url-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f7fb;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  padding: 6px 10px;
  flex-wrap: wrap;
}
.ac-url-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 2px 7px;
  border-radius: 20px;
  flex-shrink: 0;
}
.ac-url-text {
  font-size: 12px;
  color: #4338ca;
  word-break: break-all;
  flex: 1;
  text-decoration: none;
}
.ac-url-text:hover { text-decoration: underline; }
.ac-play-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.ac-play-btn:hover { background: #5b4fcf; color: #fff; }
.ac-error {
  font-size: 12px;
  color: #c0392b;
  background: #fff5f5;
  border: 1px solid #fdd;
  border-radius: 7px;
  padding: 5px 10px;
}

/* ── Mode / API Environment Card (mode-) ─────────────────────── */
.mode-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.25rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.mode-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
}
.mode-header-text {
  flex: 1;
  min-width: 0;
}
.mode-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
}
.mode-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 2px 0;
  letter-spacing: -0.01em;
}
.mode-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
}
.mode-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
}
.mode-env-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.mode-env-label--live    { color: #413b94; }
.mode-env-label--staging { color: #e24b1a; }
.mode-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f2f2f7;
  font-size: 12px;
  color: #8a8a9a;
}
.mode-warning svg { flex-shrink: 0; color: #f59e0b; }

/* ── Action Button Card (ab-) ───────────────────────────────── */
.ab-card {
  margin-top: 2rem;
  width: calc(50% - 0.75rem);
  min-width: 0;
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.ab-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}
.ab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}
.ab-header-text {
  flex: 1;
  min-width: 0;
}
.ab-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 3px 0;
  letter-spacing: -0.01em;
}
.ab-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}
.ab-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ab-save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.ab-save-btn:not(:disabled):hover {
  background: #4338ca;
}
.ab-clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 33px;
  padding: 0 13px;
  border-radius: 7px;
  border: 1.5px solid #e0e0ea;
  background: #fff;
  color: #8a8a9a;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.ab-clear-btn:not(:disabled):hover {
  border-color: #c0392b;
  color: #c0392b;
  background: #fff5f5;
}
.ab-save-btn:disabled,
.ab-clear-btn:disabled,
.ab-upload-btn:disabled,
.ab-open-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ab-loading-grid,
.ab-body {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 1.25rem;
  align-items: start;
}
.ab-preview-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.ab-preview,
.ab-preview-skeleton {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ab-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
  border: 1.5px solid #e8e8ed;
  color: #8a8a9a;
}
.ab-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ab-preview-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a8a9a;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ab-fields,
.ab-loading-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.ab-upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ab-file-label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 220px;
  background: #fafafa;
  border: 1.5px dashed #d0d0e0;
  border-radius: 9px;
  padding: 9px 14px;
  font-size: 12.5px;
  color: #666;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.ab-file-label:hover {
  border-color: #5b4fcf;
  background: #f5f3ff;
  color: #5b4fcf;
}
.ab-file-label svg {
  flex-shrink: 0;
  color: #999;
}
.ab-file-label:hover svg {
  color: #5b4fcf;
}
.ab-file-hidden {
  display: none;
}
.ab-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 82px;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}
.ab-upload-btn:not(:disabled):hover {
  background: #4338ca;
}
.ab-progress {
  width: 100%;
  height: 4px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  background: #eaeaf0;
}
.ab-progress::-webkit-progress-bar {
  background: #eaeaf0;
}
.ab-progress::-webkit-progress-value {
  background: linear-gradient(90deg, #5b4fcf, #9c8dff);
  border-radius: 4px;
}
.ab-progress::-moz-progress-bar {
  background: linear-gradient(90deg, #5b4fcf, #9c8dff);
  border-radius: 4px;
}
.ab-url-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ab-label {
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
}
.ab-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ab-input {
  flex: 1;
  min-width: 0;
  border: 1.5px solid #e0e0ea;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #1a1a2e;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.ab-input:focus {
  border-color: #5b4fcf;
}
.ab-open-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.ab-open-btn:not(:disabled):hover {
  background: #5b4fcf;
  color: #fff;
}
.ab-url-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f7fb;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  padding: 8px 12px;
}
.ab-url-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: #e8eaff;
  color: #4338ca;
  padding: 2px 7px;
  border-radius: 20px;
  flex-shrink: 0;
}
.ab-url-text {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #4338ca;
  word-break: break-all;
  text-decoration: none;
}
.ab-url-text:hover {
  text-decoration: underline;
}
.ab-empty-note {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #f7f7fb;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  padding: 8px 12px;
  color: #8a8a9a;
  font-size: 12px;
  line-height: 1.35;
}
.ab-empty-note svg {
  color: #5b4fcf;
  flex-shrink: 0;
}
.ab-alert {
  font-size: 12px;
  border-radius: 7px;
  padding: 7px 12px;
}
.ab-alert--error,
.ab-field-error {
  color: #c0392b;
  background: #fff5f5;
  border: 1px solid #fdd;
}
.ab-field-error {
  font-size: 12px;
  border-radius: 7px;
  padding: 6px 10px;
}
.ab-success-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  background: #e8f5e9;
  padding: 3px 10px;
  border-radius: 20px;
}
.ab-sk-line {
  height: 16px;
  width: 58%;
}
.ab-sk-line--wide {
  height: 38px;
  width: 100%;
}

/* ── Bottom row: Fallback + Mode side by side ───────────────── */
.bottom-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
  align-items: stretch;
}

/* ── Default Fallback Channel Card ───────────────────────────── */

.fbc-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.fbc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.fbc-header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.fbc-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}
.fbc-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 3px 0;
  letter-spacing: -0.01em;
}
.fbc-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}
/* URL display row */
.fbc-url-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f7f7fb;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.fbc-url-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 2px 7px;
  border-radius: 20px;
  flex-shrink: 0;
}
.fbc-url-label--new {
  background: #e8eaff;
  color: #4338ca;
}
.fbc-url-text {
  font-size: 12px;
  color: #4338ca;
  word-break: break-all;
  flex: 1;
  text-decoration: none;
}
.fbc-url-text:hover { text-decoration: underline; }
.fbc-play-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.fbc-play-btn:hover {
  background: #5b4fcf;
  color: #fff;
}
.fbc-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #aaa;
  margin-bottom: 1rem;
}
/* Upload zone */
.fbc-upload-area {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fbc-file-label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  background: #fafafa;
  border: 1.5px dashed #d0d0e0;
  border-radius: 9px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 12.5px;
  color: #666;
  transition: border-color 0.15s, background 0.15s;
}
.fbc-file-label:hover {
  border-color: #5b4fcf;
  background: #f5f3ff;
  color: #5b4fcf;
}
.fbc-file-label svg { flex-shrink: 0; color: #999; }
.fbc-file-label:hover svg { color: #5b4fcf; }
.fbc-file-hidden {
  display: none;
}
.fbc-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 80px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.15s, opacity 0.15s;
}
.fbc-upload-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.fbc-upload-btn:not(:disabled):hover { background: #4338ca; }
.fbc-uploading-text { font-variant-numeric: tabular-nums; }
/* Progress bar */
.fbc-progress {
  margin-top: 10px;
  height: 3px;
  border-radius: 3px;
  background: #eaeaf0;
  overflow: hidden;
}
.fbc-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #5b4fcf, #9c8dff);
  border-radius: 3px;
  transition: width 0.25s ease;
}
/* Result area */
.fbc-result {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f5;
}
.fbc-result-url {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.fbc-result-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fbc-save-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 33px;
  padding: 0 16px;
  border-radius: 7px;
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.fbc-save-btn:hover {
  background: #5b4fcf;
  color: #fff;
}
.fbc-success-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  background: #e8f5e9;
  padding: 3px 10px;
  border-radius: 20px;
}
.fbc-save-inline {
  display: flex;
  margin-top: 10px;
}
/* Alerts */
.fbc-alert {
  margin-top: 10px;
  font-size: 12px;
  color: #c0392b;
  background: #fff5f5;
  border: 1px solid #fdd;
  border-radius: 7px;
  padding: 7px 12px;
}
.fbc-alert--inline {
  margin-top: 0;
  display: inline-block;
}

.landscape {
    height: 300px;
    width: 100px;
    padding: 1rem;
}
img {
    max-width: 100%;
    max-height: 100%;
}
.tooltip {
  display: block !important;
  z-index: 10000;
}
.tooltip .tooltip-inner {
  background: #3a1f8a;
  color: white;
  border-radius: 5px;
  padding: 10px 15px 10px;
}
.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: #3a1f8a;
}
.tooltip[x-placement^="top"] {
  margin-bottom: 5px;
}
.tooltip[x-placement^="top"] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="right"] .tooltip-arrow {
  content: " ";
  position: absolute;
  top: 50%;
  right: 98%; 
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent #3a1f8a transparent transparent;
}

.tooltip[aria-hidden='true'] {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.15s, visibility 0.15s;
}
.tooltip[aria-hidden='false'] {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.15s;
}

.apiStatusStaging{
  background-color: #ff5e00;
  border: 2px solid #ff5e00;
  color: white;
  border-radius: 2rem;
  padding: 5px 15px;
  font-size: 16px;
  margin: 4px 2px;
  cursor: pointer;
  outline:0;
  float: right;
}
.apiStatusLive{
  background-color: #3b3586;
  border: 2px solid #3b3586;
  color: white;
  border-radius: 2rem;
  padding: 5px 15px;
  font-size: 16px;
  margin: 4px 2px;
  cursor: pointer;
  outline:0;
  float: right;
}
p.us-banner {
    font-size: 20px;
    font-weight: bold;
    color: #423799;
    margin-bottom: 10px;
    margin-top: 50px;
}

.tabs-component {
  margin: 1em 0;
}

.tabs-component-tabs {
  border: solid 1px #ddd;
  border-radius: 6px;
  margin-bottom: 5px;
}

/****** Add banner design***** */
.add-banner-header {
    padding: 15px 1rem;
}

.add-banner-header h2{
  font-size: 20px;
  float:left;
  margin-right: 20px;
}

.banner-preview-container {
  width: 50%;
}

/* ── Banners Card (bn-) ──────────────────────────────────────── */
.pg-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1.5rem;
  padding-top: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
}
.pg-title {
  font-family: 'Inter', 'Avenir', Helvetica, Arial, sans-serif;
  font-size: 34px;
  font-weight: 900;
  color: #3d3d52;
  letter-spacing: -0.04em;
  line-height: 1.1;
  margin: 0;
}
.pg-title-sub {
  font-size: 13px;
  color: #bbbbc8;
  margin: 5px 0 0 0;
  line-height: 1.45;
  font-weight: 400;
}
.pg-settings-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #8a8a9a;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #e8e8ed;
  background: #fafafa;
  transition: border-color 0.15s, color 0.15s;
}
.pg-settings-link:hover { border-color: #5b4fcf; color: #5b4fcf; }
.bn-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  margin-bottom: 2rem;
}
.bn-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}
.bn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}
.bn-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 3px 0;
  letter-spacing: -0.01em;
}
.bn-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}
.bn-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.bn-add-btn:hover { background: #4338ca; }
/* Grid */
.bn-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
/* ── Skeleton shimmer ─────────────────────────────────────────── */
@keyframes sk-shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}
.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}
.bn-skeleton {
  border: 1px solid #eaeaf0;
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;
}
.bn-sk-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
}
.bn-sk-badge {
  height: 18px;
  width: 72px;
  border-radius: 4px;
}
.bn-sk-actions {
  display: flex;
  gap: 6px;
}
.bn-sk-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
}
.bn-sk-image {
  width: 100%;
  aspect-ratio: 16/7;
  border-radius: 0;
}
.bn-item {
  border: 1px solid #eaeaf0;
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;
  transition: box-shadow 0.15s;
}
.bn-item:hover { box-shadow: 0 3px 12px rgba(0,0,0,0.08); }
.bn-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 6px;
  background: #fff;
  border-bottom: 1px solid #f0f0f7;
}
.bn-type-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #5b4fcf;
  background: #f0effe;
  padding: 2px 8px;
  border-radius: 20px;
}
.bn-actions {
  display: flex;
  gap: 4px;
}
.bn-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.bn-action-btn:hover { background: #f0effe; border-color: #5b4fcf; color: #5b4fcf; }
.bn-action-btn--delete:hover { background: #fff5f5; border-color: #fbb; color: #c0392b; }
.bn-image-wrap {
  width: 100%;
  aspect-ratio: 16/7;
  overflow: hidden;
  background: #f2f2f7;
}
.bn-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.bn-empty {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 2.5rem 1rem;
  color: #b0b0c0;
  font-size: 13px;
  text-align: center;
}
.bn-empty strong { color: #5b4fcf; }


@media (min-width: 700px) {
  .tabs-component-tabs {
    border: 0;
    align-items: stretch;
    display: flex;
    justify-content: flex-start;
    margin-bottom: -1px;
  }
}

.tabs-component-tab {
  color: #999;
  font-size: 14px;
  font-weight: 600;
  margin-right: 0;
  list-style: none;
}

.tabs-component-tab:not(:last-child) {
  border-bottom: dotted 1px #ddd;
}

.tabs-component-tab:hover {
  color: #666;
}

.tabs-component-tab.is-active {
  color: #ffffff;
  background: #423799;
}

.tabs-component-tab.is-disabled * {
  color: #cdcdcd;
  cursor: not-allowed !important;
}

@media (min-width: 700px) {
  .tabs-component-tab {
    background-color: #fff;
    border: solid 1px #ddd;
    border-radius: 3px 3px 0 0;
    margin-right: .5em;
    /* transform: translateY(2px); */
    transition: transform .3s ease;
  }

  .tabs-component-tab.is-active {
    border-bottom: solid 1px #fff;
    z-index: 2;
    /* transform: translateY(0); */
  }
}

.tabs-component-tab-a {
  align-items: center;
  color: inherit;
  display: flex;
  padding: .75em 1em;
  text-decoration: none;
}

.tabs-component-panels {
  padding: 2em 0;
}

@media (min-width: 700px) {
  .tabs-component-panels {
    border-top-left-radius: 0;
    background-color: #fff;
    border: solid 1px #ddd;
    border-radius: 0 6px 6px 6px;
    box-shadow: 0 0 10px rgba(0, 0, 0, .05);
    padding: 2em 2em;
  }
}

.header-controls {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 15px; /* Spacing between icons */
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

.header-icon:hover {
  opacity: 1;
}

.header-icon svg {
  display: block;
}

@media (max-width: 900px) {
  .bottom-row,
  .ab-body,
  .ab-loading-grid {
    grid-template-columns: 1fr;
  }
  .ab-card {
    width: 100%;
  }
  .ab-preview-col {
    align-items: flex-start;
  }
}

@media (max-width: 760px) {
  .ab-header,
  .ab-upload-row,
  .ab-input-wrap {
    flex-direction: column;
    align-items: stretch;
  }
  .ab-save-btn,
  .ab-upload-btn,
  .ab-open-btn {
    width: 100%;
  }
  .ab-open-btn {
    border-radius: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// Settings.vue?ae927eb4